import { tsv } from "@faire/slate/lib/tsv";
import { ModalButtons } from "@faire/slate/private/modal/ModalButtons";
import { useModalContext } from "@faire/slate/private/modal/ModalContext";
import { ModalFooterBase } from "@faire/slate/private/modal/ModalFooterBase";
import { ModalFooterTexts } from "@faire/slate/private/modal/ModalFooterTexts";
import { ModalFooterVariant } from "@faire/slate/private/modal/types";
import { assertText } from "@faire/slate/private/shared/type-utils";
import type { IText } from "@faire/slate/private/shared/types";
import React from "react";

const INLINE_FOOTER_TEXT_WIDTHS = ["large", "xlarge"];

type IProps = {
  /**
   * The children of the modal footer
   *
   * When variant is default it only accepts Button components (up to three)
   */
  children?: React.ReactNode;

  /**
   * The variant of the modal footer.
   *
   * @default "default"
   */
  variant?: ModalFooterVariant;

  /**
   * Helper text shown alongside the footer buttons. Accepts string or StrictLocalMsg.
   */
  helper?: IText;

  /**
   * Error text shown alongside the footer buttons. Accepts string or StrictLocalMsg.
   */
  error?: IText;
};

/**
 * Base modal footer component. When variant is default, accepts up to 3 Button components and only Button
 */
export const BaseModalFooter: React.FC<IProps> = ({
  variant,
  children,
  helper,
  error,
}) => {
  const { modalWidth } = useModalContext();
  const isCustomSlot = variant === "customSlot";

  assertText(
    helper,
    "BaseModalFooter accepts only string or StrictLocalMsg as helper"
  );
  assertText(
    error,
    "BaseModalFooter accepts only string or StrictLocalMsg as error"
  );

  const isInlineFooterText =
    !!modalWidth && INLINE_FOOTER_TEXT_WIDTHS.includes(modalWidth);

  if (isCustomSlot) {
    return <ModalFooterBase>{children}</ModalFooterBase>;
  }

  return (
    <ModalFooterBase>
      <div className={footerButtonsStyle.cls({ width: modalWidth })}>
        <ModalButtons>{children}</ModalButtons>
      </div>
      <ModalFooterTexts
        helper={helper}
        error={error}
        layout={isInlineFooterText ? "inline" : "stack"}
      />
    </ModalFooterBase>
  );
};

const footerButtonsStyle = tsv({
  base: "fs-modal__footer-buttons",
  variants: {
    width: {
      small: "",
      medium: "",
      large: "fs-modal__footer-buttons--large",
      xlarge: "fs-modal__footer-buttons--xlarge",
      custom: "",
    },
  },
});
